/**
 * Internal JSON-value helpers for the sd-patch diff/apply engine.
 *
 * The component tree (`IScreenDesign` / `IDisplayItem`) is JSON-serialized when it
 * travels between the designer, the collab server, and the C# backend, so the whole
 * engine works on "JSON-cloneable" values only. `CollabJsonValue` is the concrete,
 * recursive domain type used instead of `unknown` maps: keys are open-ended but the
 * value space is closed.
 *
 * Semantics shared by clone and equality:
 * - Object keys whose value is `undefined` are treated as ABSENT (plain JSON
 *   round-trips drop them, and vitest's `toEqual` ignores them), so diff/apply/clone
 *   all agree with what a serialized tree actually contains.
 * - Functions/symbols are not part of the domain; cloning drops them.
 */

export type CollabJsonValue = string | number | boolean | null | undefined | ICollabJsonObject | CollabJsonValue[];

export interface ICollabJsonObject {
  [key: string]: CollabJsonValue;
}

export function isJsonObject(value: unknown): value is ICollabJsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isJsonArray(value: unknown): value is CollabJsonValue[] {
  return Array.isArray(value);
}

/**
 * Deep-clones a JSON-cloneable value. Object keys holding `undefined` are dropped
 * (JSON semantics); non-cloneable leaves (functions, symbols) become `undefined`.
 */
export function cloneJsonValue(value: unknown): CollabJsonValue {
  if (value === null) {
    return null;
  }
  const valueType: string = typeof value;
  if (valueType === "string" || valueType === "number" || valueType === "boolean") {
    return value as string | number | boolean;
  }
  if (valueType === "undefined") {
    return undefined;
  }
  if (Array.isArray(value)) {
    const clonedArray: CollabJsonValue[] = [];
    for (const element of value) {
      clonedArray.push(cloneJsonValue(element));
    }
    return clonedArray;
  }
  if (valueType === "object") {
    const source: ICollabJsonObject = value as ICollabJsonObject;
    const clonedObject: ICollabJsonObject = {};
    for (const key of Object.keys(source)) {
      const clonedMember: CollabJsonValue = cloneJsonValue(source[key]);
      if (clonedMember !== undefined) {
        clonedObject[key] = clonedMember;
      }
    }
    return clonedObject;
  }
  return undefined;
}

function definedKeys(obj: ICollabJsonObject): string[] {
  return Object.keys(obj).filter((key: string): boolean => obj[key] !== undefined);
}

/**
 * Deep JSON equality. `undefined`-valued object keys count as absent; `NaN` equals
 * `NaN` (mirrors `toEqual` and keeps the differ stable on odd numeric payloads).
 */
export function deepJsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (typeof a === "number" && typeof b === "number") {
    return Number.isNaN(a) && Number.isNaN(b);
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepJsonEqual(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }
  if (isJsonObject(a) && isJsonObject(b)) {
    const aKeys: string[] = definedKeys(a);
    const bKeys: string[] = definedKeys(b);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    for (const key of aKeys) {
      if (!deepJsonEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }
  return false;
}
